import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { startCase, lowerCase, take } from 'lodash';
import Table, { Column } from 'components/Table';
import PageContainer from 'components/PageContainer';
import { styled, List, ListItem } from '@mui/material';
import { FirmSelector } from 'components/FirmSelector';
import AlertDialog from 'components/AlertDialog';
import { MuiDateRangePicker } from 'components/FormElements/DateRangePicker';
import { InvestorReportingType } from 'further-types/investor-reporting';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import {
  INVESTOR_REPORTING_ADD,
  ADD_CUSTOM_UPDATE,
} from 'adminConstants/routes';
import ActionTools from './ActionTools';
import { useInvestorReporting } from 'hooks/data/investorReporting/useInvestorReporting';
import { useExitStatement } from 'hooks/data/exit';
import { useCustomUpdate } from 'hooks/data/customUpdate/useCustomUpdate';
import { dateToLabel } from 'further-ui/utils';
import PageContent from 'components/PageContent';
import PageFilters, { Filter } from 'components/PageFilters';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import { Api } from 'further-types/custom-updates';

type InvestorReport = {
  _id: string;
  subject: string;
  managerOverViewTitle: string;
  updateType: string;
  status: string;
  viewable: boolean;
  sendTo: Array<string>;
  submitDate: string;
  firmName?: string;
  recipientFilter: Api.RecipientFilter;
  affectedCompanies?: Array<{ id: string; name: string }>;
};

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.green,
}));

const StyledBulletList = styled(List)(({ theme }) => ({
  paddingLeft: theme.spacing(6),
  listStyleType: 'disc',
  width: 'fit-content',
  margin: '0 auto',
  textAlign: 'left',
}));

const StyledBullet = styled(ListItem)(() => ({
  display: 'list-item',
  padding: 0,
}));

const staticColumns = (
  expandedSentTo: Set<string>,
  expandSentTo: (rowId: string) => void,
): Array<Column<InvestorReport>> => [
  {
    label: 'Update date',
    key: 'submitDate',
    sort: true,
    render: (elm) => <>{dateToLabel(elm.submitDate)}</>,
  },
  {
    label: 'Update type',
    sort: true,
    key: 'updateType',
  },
  {
    label: 'Status',
    key: 'status',
    sort: true,
    render: (elm) => <div>{startCase(lowerCase(elm.status))}</div>,
  },
  {
    label: 'Firm name',
    key: 'firmName',
    sort: false,
    render: (elm) => <>{elm.firmName} </>,
  },
  {
    label: 'Report name',
    key: 'reportName',
    sort: false,
    render: (elm) => {
      const title =
        elm.updateType === InvestorReportingType.DeploymentUpdate ||
        elm.updateType === InvestorReportingType.CustomUpdate
          ? elm.subject
          : elm.managerOverViewTitle;
      return <>{title}</>;
    },
  },
  {
    label: 'Sent to',
    key: 'sendTo',
    sort: false,
    render: (elm) => {
      switch (elm.recipientFilter) {
        case Api.RecipientFilter.All:
          return 'All registered users';
        case Api.RecipientFilter.Specific:
          return 'Specific investors';
        case Api.RecipientFilter.Subscriptions:
          return 'All registered investors with subscriptions';
        case Api.RecipientFilter.NoSubscriptions:
          return 'All registered investors without subscriptions';
      }

      if (expandedSentTo.has(elm._id)) {
        return <>{elm.sendTo.join(', ')}</>;
      }

      return (
        <>
          {take(elm.sendTo, 2).join(', ')}
          {elm.sendTo.length > 2 && (
            <>
              {', and '}
              <StyledLink
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  expandSentTo(elm._id);
                }}
              >
                {elm.sendTo.length - 2} more
              </StyledLink>
            </>
          )}
        </>
      );
    },
  },
];

const InvestorReporting: React.FC = () => {
  const { firmId, isSuperAdmin } = useGetRole();
  const deleteDisclosure = useDisclosure();
  const [
    canCreatePeriodic,
    canCreateCustom,
    canEditPeriodic,
    canEditCustom,
    canEditExitStatement,
    canDeletePeriodic,
    canDeleteCustom,
    canDeleteExitStatement,
    canPublishPeriodic,
    canPublishCustom,
  ] = useHasPermissions([
    RolePermission.CreateReportPeriodic,
    RolePermission.CreateReportCustom,
    RolePermission.EditReportPeriodic,
    RolePermission.EditReportCustom,
    RolePermission.EditReportExitStatement,
    RolePermission.DeleteReportPeriodic,
    RolePermission.DeleteReportCustom,
    RolePermission.DeleteReportExitStatement,
    RolePermission.PublishReportPeriodic,
    RolePermission.PublishReportCustom,
  ]);
  const [expandedSentTo, setExpandedSentTo] = useState<Set<string>>(new Set());

  const updateTypeOptions = Object.entries(InvestorReportingType).map(
    ([_, value]) => ({
      name: value,
      key: value,
    }),
  );
  const updateTypeOptionKeys = updateTypeOptions.map((opt) => opt.key);

  const {
    page,
    order,
    orderBy,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    toFirstPage,
    handleRequestSort,
  } = usePagination({
    id: 'investor-reporting',
    orderBy: 'submitDate',
    order: 'desc',
  });

  const { filters, handleChangeFilters } = useFilters<{
    startDate?: Date;
    endDate?: Date;
    firmId?: string;
    updateTypes?: Array<string>;
  }>('investor-reporting', {
    defaultFilters: {
      startDate: undefined,
      endDate: undefined,
      firmId,
      updateTypes: updateTypeOptionKeys,
    },
    onFiltersChange: toFirstPage,
  });

  const onDeleteSuccess = () => {
    deleteDisclosure.onClose();
  };

  const {
    fetchInvestorReports,
    deleteInvestorReport: { mutate: deleteReport, isPending: isRemoving },
  } = useInvestorReporting({
    onDeleteSuccess,
  });

  const { deleteExitStatement } = useExitStatement({
    onDeleteSuccess,
  });

  const {
    deleteCustomUpdate: {
      mutate: deleteCustomUpdate,
      isPending: isRemovingCustomUpdate,
    },
  } = useCustomUpdate({
    onDeleteSuccess,
  });

  const { data, isFetching } = fetchInvestorReports({
    page,
    perPage: rowsPerPage,
    type: order,
    keyName: orderBy,
    firmId: filters?.firmId,
    startDate: filters?.startDate,
    endDate: filters?.endDate,
    updateTypes: filters?.updateTypes,
  });

  const handleUpdateTypesChange = (value: Array<string>) => {
    if (!value.includes('all')) {
      handleChangeFilters({ updateTypes: value });
      return;
    }

    if (filters?.updateTypes?.length !== updateTypeOptions.length) {
      handleChangeFilters({ updateTypes: updateTypeOptionKeys });
      return;
    }

    handleChangeFilters({ updateTypes: [] });
  };

  const expandSentTo = (rowId: string) => {
    setExpandedSentTo((prev) => {
      const newSet = new Set(prev);
      newSet.add(rowId);
      return newSet;
    });
  };

  return (
    <PageContainer
      heading="Investor Reporting"
      breadcrumbs={[
        { label: 'Dashboard', link: '/' },
        { label: 'Investor Reporting', isActive: true },
      ]}
    >
      <PageContent>
        <PageFilters
          pageId="investor-reporting"
          buttons={[
            {
              visible: canCreatePeriodic,
              label: 'Create periodic update',
              link: INVESTOR_REPORTING_ADD,
            },
            {
              visible: canCreateCustom,
              label: 'Create custom update',
              link: ADD_CUSTOM_UPDATE,
            },
          ]}
        >
          {isSuperAdmin && (
            <Filter>
              <FirmSelector
                firmId={filters?.firmId}
                onChange={(firmId) => {
                  handleChangeFilters({ firmId });
                }}
                placeholder="Search by firm"
              />
            </Filter>
          )}
          <Filter>
            <AppSelectBox
              multiple
              fullWidth
              valueKey="key"
              labelKey="name"
              placeholder="Update type"
              variant="outlined"
              data={updateTypeOptions}
              value={Array.from(new Set(filters.updateTypes ?? []))}
              onChange={(event) => {
                handleUpdateTypesChange(event.target.value as Array<string>);
              }}
              renderValue={(value) => {
                const selected = value as Array<string>;

                if (selected.length === 0) {
                  return 'Select update types';
                }

                if (selected.length === updateTypeOptions.length) {
                  return 'All update types';
                }

                return selected
                  .map(
                    (type) =>
                      updateTypeOptions.find((option) => option.key === type)
                        ?.name,
                  )
                  .join(', ');
              }}
            />
          </Filter>
          <Filter>
            <MuiDateRangePicker
              dateRange={filters}
              onDateRangeChange={(dateRange) => {
                handleChangeFilters(dateRange);
              }}
              placeholder="Select date"
            />
          </Filter>
        </PageFilters>

        <Table
          columns={[
            ...staticColumns(expandedSentTo, expandSentTo),
            {
              label: 'Tools',
              key: 'actions',
              sort: false,
              render: (elm) => (
                <ActionTools
                  investorReport={elm}
                  canEditPeriodic={canEditPeriodic}
                  canEditCustom={canEditCustom}
                  canEditExitStatement={canEditExitStatement}
                  canDeletePeriodic={canDeletePeriodic}
                  canDeleteCustom={canDeleteCustom}
                  canDeleteExitStatement={canDeleteExitStatement}
                  deleteDisclosure={deleteDisclosure}
                  canPublishPeriodic={canPublishPeriodic}
                  canPublishCustom={canPublishCustom}
                />
              ),
            },
          ]}
          onRequestSort={handleRequestSort}
          order={order}
          orderBy={orderBy}
          tablebody={data?.investorReportings ?? []}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          count={data?.total ?? 0}
          pagination={true}
          loading={isFetching}
          variant="nohover"
        />
      </PageContent>

      <AlertDialog
        onClose={deleteDisclosure.onClose}
        open={deleteDisclosure.isOpen}
        title={`Are you sure you want to delete this ${
          deleteDisclosure.actionProps?.isExitStatement
            ? 'exit statement'
            : deleteDisclosure.actionProps?.isCustomUpdate
            ? 'custom update'
            : 'investor report'
        }?`}
        content={
          <>
            <div>
              {`This action cannot be undone and all information relating to the ${
                deleteDisclosure.actionProps?.isExitStatement
                  ? 'exit statement'
                  : deleteDisclosure.actionProps?.isCustomUpdate
                  ? 'custom update'
                  : 'investor report'
              } will be lost.`}
            </div>
            {deleteDisclosure.actionProps?.isPeriodicUpdate &&
              deleteDisclosure.actionProps?.affectedCompanies?.length > 0 && (
                <div>
                  <br />

                  <div>
                    One or more companies had their share price updated in this
                    report. Deleting this report will also revert the share
                    price changes for the following companies:
                  </div>
                  <StyledBulletList>
                    {deleteDisclosure.actionProps.affectedCompanies.map(
                      (company) => (
                        <StyledBullet key={company.id}>
                          {company.name}
                        </StyledBullet>
                      ),
                    )}
                  </StyledBulletList>
                </div>
              )}
          </>
        }
        btnLabels={{
          confirm: `Delete ${
            deleteDisclosure.actionProps?.isExitStatement
              ? 'exit statement'
              : deleteDisclosure.actionProps?.isCustomUpdate
              ? 'custom update'
              : 'investor report'
          }`,
          cancel: 'Go back',
        }}
        cancelBtnProps={{
          variant: 'contained',
          color: 'primary',
          sx: { minWidth: '180px !important' },
        }}
        confirmBtnProps={{
          variant: 'outlined',
          color: 'primary',
          disabled:
            isRemoving ||
            deleteExitStatement.isPending ||
            isRemovingCustomUpdate,
        }}
        onConfirm={() => {
          if (deleteDisclosure.actionProps?.isExitStatement) {
            deleteExitStatement.mutate({
              exitStatementId: deleteDisclosure.actionProps.id,
            });
          } else if (deleteDisclosure.actionProps?.isCustomUpdate) {
            deleteCustomUpdate(deleteDisclosure.actionProps.id);
          } else {
            deleteReport(deleteDisclosure.actionProps.id);
          }
        }}
      />
    </PageContainer>
  );
};

export default InvestorReporting;
